// Chakra imports
import { Box, Flex, Text, useColorModeValue } from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import ContentLoader from "components/loader/ContentLoader";
import LineChart from "components/Charts/LineChart";
import { lineChartOptions } from "variables/charts";
import React, { useEffect, useMemo, useState } from "react";
import { fetchSalesOverview } from "../../services";

const SalesOverview = ({ title = "Sales Overview", percentage = 5 }) => {
  const textColor = useColorModeValue("gray.700", "white");

  const [numbers, setNumbers] = useState([]);
  const [months, setMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadSalesOverview = async () => {
      setIsLoading(true);
      try {
        const response = await fetchSalesOverview();
        if (active && response.status === 200) {
          // Backend: { numbers: [...], months: [...] }
          setNumbers(response.data?.numbers || []);
          setMonths(response.data?.months || []);
        }
      } catch (e) {
        console.error("Sales overview load failed:", e);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    loadSalesOverview();
    return () => {
      active = false;
    };
  }, []);

  // Only the web series is shown. The mobile series is intentionally disabled
  // for the web dashboard.
  const chartSeries = useMemo(
    () => [
      // { name: "Mobile apps", data: numbers },
      { name: "Websites", data: numbers },
    ],
    [numbers]
  );

  // Drive the x-axis labels from the API months, keeping the rest of the
  // default styling. Fall back to the static categories when none are returned.
  const chartOptions = useMemo(
    () => ({
      ...lineChartOptions,
      xaxis: {
        ...lineChartOptions.xaxis,
        type: "category",
        categories: months.length ? months : lineChartOptions.xaxis.categories,
      },
    }),
    [months]
  );

  const currentYear = new Date().getFullYear();

  return (
    <Card p='28px 10px 16px 0px' mb={{ sm: "26px", lg: "0px" }}>
      <CardHeader mb='20px' pl='22px'>
        <Flex direction='column' alignSelf='flex-start'>
          <Text fontSize='lg' color={textColor} fontWeight='bold' mb='6px'>
            {title}
          </Text>
          <Text fontSize='md' fontWeight='medium' color='gray.400'>
            <Text
              as='span'
              color={percentage > 0 ? "green.400" : "red.400"}
              fontWeight='bold'>
              {`${percentage}%`} more
            </Text>{" "}
            in {currentYear}
          </Text>
        </Flex>
      </CardHeader>
      <Box w='100%' h={{ sm: "300px" }} ps='8px'>
        {isLoading ? (
          <ContentLoader minH='300px' label='Loading chart...' />
        ) : (
          <LineChart series={chartSeries} options={chartOptions} />
        )}
      </Box>
    </Card>
  );
};

export default SalesOverview;
